import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Box, Fade } from '@material-ui/core';
import { useReportContext } from 'contexts/report/ReportContext';
import { useReportAccountContext } from 'contexts/report/ReportAccountContext';
import reportLocales from 'globals/locales/report';
import { isSuccessState, wait } from 'globals/utils';
import FsTrendChart from 'ui-elements/charts/FsTrendChart';
import FsBalanceChart from 'ui-elements/charts/FsBalanceChart';
import FsDefaultFallbackComponent from 'ui-elements/loaders/FsDefaultFallbackComponent';
import FsInnerTabPanel from 'ui-elements/tabs/FsInnerTabPanel';
import { ChartContainerBox } from './AccountChartsSection.style-elements';

// Locales
const { tabLabels } = reportLocales.account.chartSection;

const AccountChartsSection: React.FC = () => {
  const {
    report: {
      bankAccountsFinancialStats: { status, incomesOutgoings, balances },
      setBankAccountsFinancialStats,
    },
  } = useReportContext();
  const { activeAccountId, activeDateRange } = useReportAccountContext();
  const [tabIdx, setTabIdx] = React.useState<number>(0);
  const [chartIdx, setChartIdx] = React.useState<number>(0);
  const [tabChanging, setTabChanging] = React.useState<boolean>(false);

  const isChartIdx = (idx: number): boolean => {
    return idx === chartIdx;
  };

  const handleChange = async (
    event: React.ChangeEvent<Record<string, unknown>>,
    newTabIdx: number
  ): Promise<void> => {
    setTabChanging(true);
    setTabIdx(newTabIdx);
    await wait(300);
    setChartIdx(newTabIdx);
    setTabChanging(false);
  };

  useEffect(() => {
    if (activeAccountId || activeDateRange) {
      setBankAccountsFinancialStats();
    }
  }, [activeAccountId, activeDateRange, setBankAccountsFinancialStats]);

  if (!isSuccessState(status))
    return (
      <FsDefaultFallbackComponent
        status={status}
        data-test="default-fallback-component"
        withShadow={false}
      />
    );
  return (
    <>
      <FsInnerTabPanel
        labels={tabLabels}
        onChange={handleChange}
        value={tabIdx}
      />
      <ChartContainerBox data-test="bank-accounts-financial-stats-container">
        <Box minWidth={800} padding={2}>
          <Fade in={!tabChanging} timeout={200}>
            <Box>
              {isChartIdx(0) && (
                <FsBalanceChart
                  chartData={balances()}
                  data-test="balance-chart"
                />
              )}
              {isChartIdx(1) && (
                <FsTrendChart
                  chartData={incomesOutgoings()}
                  data-test="trend-chart"
                />
              )}
            </Box>
          </Fade>
        </Box>
      </ChartContainerBox>
    </>
  );
};

export default observer(AccountChartsSection);
